import React, { useState, useEffect } from 'react';
import { 
    Users, 
    Search, 
    Edit3, 
    Trash2, 
    Shield, 
    ShieldCheck, 
    Crown,
    UserCheck, 
    UserX, 
    Mail,
    Calendar,
    MapPin,
    Music,
    Video,
    Image,
    Eye,
    EyeOff,
    Save,
    X,
    AlertTriangle,
    CheckCircle,
    User,
    Filter,
    Palette
} from 'lucide-react';
import api from '../services/api';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/admin/users');
            
            if (response.data.success) {
                setUsers(response.data.data);
            } else {
                setError('Failed to load users');
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Unable to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = (user) => {
        setEditingUser(user.id);
        setEditForm({
            display_name: user.display_name || '',
            email: user.email || '',
            bio: user.bio || '',
            location: user.location || '',
            is_verified: user.is_verified || false,
            is_admin: user.is_admin || false,
            is_artist: user.is_artist || false,
            is_official_artist: user.is_official_artist || false,
            crew_role: user.crew_role || '',
            crew_bio: user.crew_bio || ''
        });
    };

    const handleSaveUser = async (userId) => {
        try {
            setActionLoading(true);
            const response = await api.put(`/admin/users/${userId}`, editForm);
            
            if (response.data.success) {
                setUsers(users.map(user => 
                    user.id === userId ? { ...user, ...editForm } : user
                ));
                setEditingUser(null);
                setEditForm({});
            } else {
                setError('Failed to update user');
            }
        } catch (err) {
            console.error('Error updating user:', err);
            setError('Unable to update user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            setActionLoading(true);
            const response = await api.delete(`/admin/users/${userId}`);
            
            if (response.data.success) {
                setUsers(users.filter(user => user.id !== userId));
                setDeleteConfirm(null);
            } else {
                setError('Failed to delete user');
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            setError('Unable to delete user');
        } finally {
            setActionLoading(false);
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = filterRole === 'all' ||
            (filterRole === 'admin' && user.is_admin) ||
            (filterRole === 'artist' && user.is_artist) ||
            (filterRole === 'official_artist' && user.is_official_artist) ||
            (filterRole === 'crew' && user.is_crew_member) ||
            (filterRole === 'regular' && !user.is_admin && !user.is_artist && !user.is_official_artist && !user.is_crew_member);
        
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'verified' && user.is_verified) ||
            (filterStatus === 'unverified' && !user.is_verified);

        return matchesSearch && matchesRole && matchesStatus;
    });

    const formatNumber = (num) => {
        if (!num) return '0';
        if (num < 1000) return num.toString();
        if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
        return `${(num / 1000000).toFixed(1)}M`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Users className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading user management...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Users className="w-8 h-8 text-blue-400" />
                        <h1 className="text-4xl font-black gradient-text">User Management</h1>
                    </div>
                    <p className="text-gray-400">
                        Manage ensemble members, edit user information, and control access permissions
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <p className="text-red-400">{error}</p>
                        <button 
                            onClick={() => setError(null)}
                            className="ml-auto text-red-400 hover:text-red-300"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Filters and Search */}
                <div className="glass-card p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search users by name, username, or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Role Filter */}
                        <div className="flex items-center gap-2">
                            <Filter className="w-5 h-5 text-gray-400" />
                            <select
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Roles</option>
                                <option value="admin">Admins</option>
                                <option value="crew">Crew Members</option>
                                <option value="artist">Artists</option>
                                <option value="official_artist">Official Artists</option>
                                <option value="regular">Regular Users</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Status</option>
                                <option value="verified">Verified</option>
                                <option value="unverified">Unverified</option>
                            </select>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-700">
                        <div className="text-center">
                            <span className="text-2xl font-bold text-blue-400">{filteredUsers.length}</span>
                            <p className="text-gray-400 text-sm">Total Users</p>
                        </div>
                        <div className="text-center">
                            <span className="text-2xl font-bold text-green-400">
                                {filteredUsers.filter(u => u.is_verified).length}
                            </span>
                            <p className="text-gray-400 text-sm">Verified</p>
                        </div>
                        <div className="text-center">
                            <span className="text-2xl font-bold text-yellow-400">
                                {filteredUsers.filter(u => u.is_admin).length}
                            </span>
                            <p className="text-gray-400 text-sm">Admins</p>
                        </div>
                        <div className="text-center">
                            <span className="text-2xl font-bold text-purple-400">
                                {filteredUsers.filter(u => u.is_crew_member).length}
                            </span>
                            <p className="text-gray-400 text-sm">Crew</p>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="glass-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-800/50 border-b border-gray-700">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">User</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Role & Status</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Content</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Joined</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-800/30 transition-colors">
                                        {editingUser === user.id ? (
                                            // Editing Row
                                            <td colSpan="5" className="px-6 py-4">
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                                                            <input
                                                                type="text"
                                                                value={editForm.display_name}
                                                                onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                                                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                                                            <input
                                                                type="email"
                                                                value={editForm.email}
                                                                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                                                            <input
                                                                type="text"
                                                                value={editForm.location}
                                                                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                                                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-300 mb-2">Crew Role</label>
                                                            <input
                                                                type="text"
                                                                value={editForm.crew_role}
                                                                onChange={(e) => setEditForm({...editForm, crew_role: e.target.value})}
                                                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                                                placeholder="Leave empty if not crew member"
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-300 mb-2">Bio</label>
                                                        <textarea
                                                            value={editForm.bio}
                                                            onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                                            rows={3}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-300 mb-2">Crew Bio</label>
                                                        <textarea
                                                            value={editForm.crew_bio}
                                                            onChange={(e) => setEditForm({...editForm, crew_bio: e.target.value})}
                                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                                            rows={2}
                                                            placeholder="Crew-specific bio (only if crew member)"
                                                        />
                                                    </div>

                                                    <div className="flex flex-wrap gap-4">
                                                        <label className="flex items-center gap-2 text-gray-300">
                                                            <input
                                                                type="checkbox"
                                                                checked={editForm.is_verified}
                                                                onChange={(e) => setEditForm({...editForm, is_verified: e.target.checked})}
                                                                className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                                                            />
                                                            Verified User
                                                        </label>
                                                        <label className="flex items-center gap-2 text-gray-300">
                                                            <input
                                                                type="checkbox"
                                                                checked={editForm.is_admin}
                                                                onChange={(e) => setEditForm({...editForm, is_admin: e.target.checked})}
                                                                className="rounded border-gray-600 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-gray-800"
                                                            />
                                                            Admin Access
                                                        </label>
                                                        <label className="flex items-center gap-2 text-gray-300">
                                                            <input
                                                                type="checkbox"
                                                                checked={editForm.is_artist}
                                                                onChange={(e) => setEditForm({...editForm, is_artist: e.target.checked})}
                                                                className="rounded border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                                                            />
                                                            Artist Profile
                                                        </label>
                                                        <label className="flex items-center gap-2 text-gray-300">
                                                            <input
                                                                type="checkbox"
                                                                checked={editForm.is_official_artist}
                                                                onChange={(e) => setEditForm({...editForm, is_official_artist: e.target.checked})}
                                                                className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                                                            />
                                                            Official Artist (download access)
                                                        </label>
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleSaveUser(user.id)}
                                                            disabled={actionLoading}
                                                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                                        >
                                                            <Save className="w-4 h-4" />
                                                            Save Changes
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingUser(null);
                                                                setEditForm({});
                                                            }}
                                                            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                                                        >
                                                            <X className="w-4 h-4" />
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        ) : (
                                            // Regular Row
                                            <>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-600">
                                                            {user.profile_picture_url ? (
                                                                <img
                                                                    src={user.profile_picture_url}
                                                                    alt={user.display_name || user.username}
                                                                    width="48"
                                                                    height="48"
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                                                                    <span className="text-2xl leading-none">🌊</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-white">
                                                                {user.display_name || user.username || 'Unnamed User'}
                                                            </p>
                                                            <p className="text-gray-400 text-sm">@{user.username}</p>
                                                            <p className="text-gray-500 text-xs">{user.email}</p>
                                                            {user.location && (
                                                                <div className="flex items-center gap-1 mt-1">
                                                                    <MapPin className="w-3 h-3 text-gray-500" />
                                                                    <span className="text-gray-500 text-xs">{user.location}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {!!user.is_admin && (
                                                            <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                                                                <Crown className="w-3 h-3" />
                                                                Admin
                                                            </span>
                                                        )}
                                                        {!!user.is_crew_member && (
                                                            <span className="px-2 py-1 bg-orange-600/20 text-orange-400 text-xs rounded-full">
                                                                {user.crew_role || 'Crew'}
                                                            </span>
                                                        )}
                                                        {!!user.is_artist && (
                                                            <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                                                                <Music className="w-3 h-3" />
                                                                Artist
                                                            </span>
                                                        )}
                                                        {!!user.is_official_artist && (
                                                            <span className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                                                                <Palette className="w-3 h-3" />
                                                                Official Artist
                                                            </span>
                                                        )}
                                                        <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${
                                                            user.is_verified 
                                                                ? 'bg-green-600/20 text-green-400'
                                                                : 'bg-gray-600/20 text-gray-400'
                                                        }`}>
                                                            {user.is_verified ? (
                                                                <>
                                                                    <CheckCircle className="w-3 h-3" />
                                                                    Verified
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Eye className="w-3 h-3" />
                                                                    Unverified
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-3 text-sm text-gray-400">
                                                        {user.upload_counts?.audio > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <Music className="w-4 h-4 text-blue-400" />
                                                                <span>{user.upload_counts.audio}</span>
                                                            </div>
                                                        )}
                                                        {user.upload_counts?.video > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <Video className="w-4 h-4 text-green-400" />
                                                                <span>{user.upload_counts.video}</span>
                                                            </div>
                                                        )}
                                                        {user.upload_counts?.image > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <Image className="w-4 h-4 text-purple-400" />
                                                                <span>{user.upload_counts.image}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1 text-gray-400 text-sm">
                                                        <Calendar className="w-4 h-4" />
                                                        <span>
                                                            {new Date(user.created_at).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <button
                                                            onClick={() => handleEditUser(user)}
                                                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                                                            title="Edit user"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteConfirm(user)}
                                                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                                            title="Delete user"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredUsers.length === 0 && (
                        <div className="text-center py-12">
                            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-gray-300 mb-2">No users found</h3>
                            <p className="text-gray-400">
                                {searchTerm || filterRole !== 'all' || filterStatus !== 'all' 
                                    ? 'Try adjusting your search or filters.'
                                    : 'No users are currently registered.'
                                }
                            </p>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-gray-800 rounded-xl p-6 max-w-md mx-4 border border-red-500/30">
                            <div className="flex items-center gap-3 mb-4">
                                <AlertTriangle className="w-6 h-6 text-red-400" />
                                <h3 className="text-xl font-bold text-white">Delete User</h3>
                            </div>
                            <p className="text-gray-300 mb-6">
                                Are you sure you want to delete <span className="font-semibold text-white">
                                    {deleteConfirm.display_name || deleteConfirm.username}
                                </span>? This action cannot be undone and will permanently remove all their data.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleDeleteUser(deleteConfirm.id)}
                                    disabled={actionLoading}
                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    {actionLoading ? 'Deleting...' : 'Delete User'}
                                </button>
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    disabled={actionLoading}
                                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagement;